import { pipe, merge, pickAll, path, assocPath, when, propEq, assoc, map, pathEq, isNil, always, pathOr, uniq, append, reject, equals, prop, defaultTo, over, lensPath, join, mergeDeepLeft, either, isEmpty, match, head, set, ifElse, toPairs } from 'ramda';
import { FETCH } from 'redux-effects-fetch';
import { bind } from 'redux-effects';
import { v4 } from 'uuid';
import { schema, normalize } from 'normalizr';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var RESPONSE_CACHED = 'api-client/http/RESPONSE_CACHED';
var START_TRACKING_REQUEST = 'api-client/http/START_TRACKING_REQUEST';
var STOP_TRACKING_REQUEST = 'api-client/http/STOP_TRACKING_REQUEST';
function cacheResponse(key, expiresAt, response) {
  return {
    type: RESPONSE_CACHED,
    payload: {
      key: key,
      expiresAt: expiresAt,
      headers: response.headers.raw ? response.headers.raw() : _objectSpread2({}, response.headers),
      status: response.status,
      value: response.value
    }
  };
}
function startTrackingRequest(request) {
  return {
    type: START_TRACKING_REQUEST,
    payload: request
  };
}
function stopTrackingRequest(request) {
  return {
    type: STOP_TRACKING_REQUEST,
    payload: request
  };
}
var initialState = {
  requests: [],
  responses: {}
};
function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
  var action = arguments.length > 1 ? arguments[1] : undefined;

  switch (action.type) {
    case RESPONSE_CACHED:
      {
        return _objectSpread2(_objectSpread2({}, state), {}, {
          responses: _objectSpread2(_objectSpread2({}, state.responses), {}, _defineProperty({}, action.payload.key, {
            data: {
              headers: action.payload.headers,
              status: action.payload.status,
              value: action.payload.value
            },
            expiresAt: action.payload.expiresAt
          }))
        });
      }

    case START_TRACKING_REQUEST:
      {
        return assoc('requests', uniq(append(action.payload, state.requests)), state);
      }

    case STOP_TRACKING_REQUEST:
      {
        return assoc('requests', reject(equals(action.payload), state.requests), state);
      }

    default:
      return state;
  }
}
function getPendingRequests(state) {
  return prop('requests', state);
}
function getCachedResponse(state, props) {
  var cache = pathOr({
    expiresAt: 0
  }, ['responses', props.key], state);

  if (cache.expiresAt >= props.now) {
    return cache.data;
  }

  return null;
}

/**
 * Action type for API actions.
 * @constant
 * @type {string}
 */

var EFFECT_FETCH_API = 'EFFECT_FETCH_API';

function noop() {}

function withCompletion(uuid, action) {
  var injectUuid = assocPath(['meta', '@rushplay/api-client', 'completed'], uuid);

  if (Array.isArray(action)) {
    // Append NOOP action to the end of array
    // Altering last action might fail if it happens to be a falsy value when
    // conditional dispatching is used
    return append(injectUuid({
      type: 'NOOP'
    }), action);
  }

  return injectUuid(defaultTo({
    type: 'NOOP'
  }, action));
}

function withOnBeforeFailure(onBeforeFailure, action) {
  return over(lensPath(['meta', 'steps', 0, 1]), function () {
    var failure = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : noop;
    return function (response) {
      return onBeforeFailure(response, function () {
        return failure(response);
      });
    };
  }, action);
}

function toCacheKey(action) {
  var method = path(['payload', 'params', 'method'], action);
  var url = path(['payload', 'url'], action);
  var version = path(['payload', 'params', 'headers', 'Accept'], action);

  if (method === 'GET') {
    return join(':', [method, url, version]);
  }

  if (method === 'POST' && url === '/games/search') {
    var body = path(['payload', 'params', 'body'], action);
    return join(':', [method, url, version, body]);
  }
}

function toFetch(_ref, action) {
  var cacheFor = _ref.cacheFor,
      cacheKey = _ref.cacheKey,
      countryCode = _ref.countryCode,
      dispatch = _ref.dispatch,
      host = _ref.host,
      token = _ref.token,
      headers = _ref.headers,
      uuid = _ref.uuid;
  var subaction = action.payload;
  var nextAction = over(lensPath(['meta', 'steps']), // Steps is array of arrays with callbacks, therefore we need to
  // R.map inside another R.map
  function (steps) {
    return map(map(function () {
      var actionCreator = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : noop;
      return function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        var action = actionCreator.apply(void 0, args);
        var cacheable = cacheKey != null && cacheFor > 0;

        if (cacheable) {
          var expiresAt = Date.now() + cacheFor;
          var response = args[0];

          if (response.status >= 200 && response.status <= 299) {
            dispatch(cacheResponse(cacheKey, expiresAt, response));
          }
        }
        /* istanbul ignore next: defensive code */


        if (uuid == null) {
          return action;
        }

        return withCompletion(uuid, action);
      };
    }), defaultTo([], steps));
  }, action);
  var nextSubaction = mergeDeepLeft({
    type: FETCH,
    payload: {
      params: {
        headers: reject(either(isNil, isEmpty), _objectSpread2({
          Authorization: pathOr(token, ['payload', 'params', 'headers', 'Authorization'], subaction),
          'Frontend-Country-Code': countryCode
        }, headers))
      },
      url: "".concat(host).concat(subaction.payload.url)
    }
  }, subaction);
  return assoc('payload', nextSubaction, nextAction);
}
/**
 * Redux Effects middleware.
 * @param {object} options
 * @param {string|Function} options.host
 *   Host URL e.g. `https://api.domain.com`
 * @param {number} [options.defaultCacheFor=0]
 *   Default cache duration in milliseconds for cacheable requests
 * @param {Function} [options.countryCodeSelector]
 *   Function that gets Redux state and returns country code
 * @param {Function} [options.tokenSelector]
 *   Function that gets Redux state and returns a session token
 * @param {Function} [options.headersSelector]
 *   Function that gets Redux state and returns additional headers object
 * @param {Function} [options.substateSelector]
 *   Function that gets Redux state and returns the API substate for caching
 * @param {Function} [options.onBeforeFailure]
 *   Function called before action's `failure` callback; receives response and
 *   `next` callback to execute action's `failure` callback if necessary.
 */


function middleware(options) {
  var defaultCacheFor = typeof options.defaultCacheFor === 'number' ? options.defaultCacheFor : 0;
  var getCountryCode = typeof options.countryCodeSelector === 'function' ? options.countryCodeSelector : function () {};
  var getHost = typeof options.host === 'function' ? options.host : function () {
    return options.host;
  };
  var getToken = typeof options.tokenSelector === 'function' ? options.tokenSelector : function () {};
  var getHeaders = typeof options.headersSelector === 'function' ? options.headersSelector : function () {};
  var onBeforeFailure = typeof options.onBeforeFailure === 'function' ? options.onBeforeFailure : function (response, next) {
    return next();
  };
  return function (store) {
    return function (next) {
      return function (action) {
        if (action.type === 'EFFECT_COMPOSE' && action.payload.type === EFFECT_FETCH_API) {
          var state = store.getState();
          var subaction = action.payload;
          var cacheFor = pathOr(defaultCacheFor, ['meta', '@rushplay/api-client', 'cacheFor'], subaction);
          var cacheKey = toCacheKey(subaction);

          var _uuid = path(['meta', 'uuid'], subaction);

          var nextAction = toFetch({
            cacheFor: cacheFor,
            cacheKey: cacheKey,
            countryCode: getCountryCode(state),
            dispatch: store.dispatch,
            host: getHost(state),
            token: getToken(state),
            headers: getHeaders(state),
            uuid: _uuid
          }, withOnBeforeFailure(onBeforeFailure, action));
          var cacheable = cacheKey != null && cacheFor > 0;

          if (cacheable) {
            var substate = options.substateSelector(state);
            var now = Date.now();
            var cachedResponse = getCachedResponse(substate, {
              key: cacheKey,
              now: now
            });

            if (cachedResponse) {
              var success = pathOr(noop, ['meta', 'steps', 0, 0], nextAction);
              return store.dispatch(success(cachedResponse));
            }
          }
          /* istanbul ignore next: defensive code */


          if (_uuid == null) {
            return next(nextAction);
          }

          store.dispatch(startTrackingRequest(_uuid));
          return next(nextAction);
        }

        var dispatched = next(action);
        var uuid = path(['meta', '@rushplay/api-client', 'completed'], action);
        /* istanbul ignore next: defensive code */

        if (uuid == null) {
          return dispatched;
        }

        store.dispatch(stopTrackingRequest(uuid));
        return dispatched;
      };
    };
  };
}

/**
 * Request configuration object
 *
 * Used to create action for `redux-effects-fetch`. `success` and `failure`
 * action creators are called with response object:
 *   - `url` - The URL of the endpoint you requested (as returned by the request)
 *   - `status` - The numerical status code of the response (e.g. `200`)
 *   - `statusText` - The text version of the status (e.g. `OK`)
 *   - `headers` - A [Headers object](https://developer.mozilla.org/en-US/docs/Web/API/Headers)
 *   - `value` - The deserialized value of the response. This may be an object or string, depending on the type of response (json or text)
 *
 * @typedef {Object} FetchConfig
 * @prop {Function} [success] - Success action creator
 * @prop {Function} [failure] - Failure action creator
 * @prop {string} [token] - Security token
 * @prop {bool} [normalize] - Specifies if response should be normalized. See https://github.com/paularmstrong/normalizr for more details
 * @prop {number} [version] - API endpoint version
 * @prop {number} [cacheFor] - Specifies for how many ms response should be cached. Can be Infinity
 * @prop {Object} [params] - Request params that goes to actual fetch
 * @prop {number} [params.timeout] -  Request timeout in ms, request promise will be rejected
 * if request does not complete in specified amount of time
 */
var defaultParams = {
  headers: {
    'Content-Type': 'application/json'
  }
};
/**
 * Add authorization token as header (if present) to `fetch` params
 *
 * @private
 * @param {Object} params - `fetch` params
 * @returns `fetch` params with `Authorization` header
 */

function withAuthorizationHeader(token) {
  if (!token) {
    return defaultParams;
  }

  var authorizationHeaderLens = lensPath(['headers', 'Authorization']);
  return set(authorizationHeaderLens, token, defaultParams);
}
/**
 * Add API version as header (if present) to `fetch` params
 *
 * @private
 * @param {Object} params - `fetch` params
 * @returns `fetch` params with `Accept` header
 */


function withVersionHeader(version, params) {
  if (!version) {
    return params;
  }

  var versionHeaderLens = lensPath(['headers', 'Accept']);
  return set(versionHeaderLens, "application/vnd.casinosaga.v".concat(version), params);
}

function request(url, config) {
  var params = Object.assign({}, withVersionHeader(config.version, withAuthorizationHeader(config.token)), config.params);

  if (params.body) {
    params.body = JSON.stringify(params.body);
  }

  var uuid = v4();
  return bind({
    type: EFFECT_FETCH_API,
    payload: {
      url: url,
      params: params
    },
    meta: {
      '@rushplay/api-client': {
        cacheFor: config.cacheFor
      },
      uuid: uuid
    }
  }, config.success, config.failure);
}
/**
 * Function for constructing HTTP GET action which is used in redux-effects-fetch
 *
 * @private
 * @param {String} request url
 * @param {FetchConfig} config
 * @returns {Object} action object which is used for HTTP GET request
 */


function getRequest(url, config) {
  var params = getParams(config, 'GET');
  return request(url, params);
}
/**
 * Function for constructing HTTP POST action which is used in redux-effects-fetch
 *
 * @private
 * @param {string} request url
 * @param {FetchConfig} config
 * @returns {Object} action  which is used for HTTP POST request
 */


function postRequest(url, config) {
  var params = getParams(config, 'POST');
  return request(url, params);
}
/**
 * Function for constructing HTTP PUT action which is used in redux-effects-fetch
 *
 * @private
 * @param {String} request url
 * @param {FetchConfig} config
 * @returns {Object} action  which is used for HTTP PUT request
 */


function putRequest(url, config) {
  var params = getParams(config, 'PUT');
  return request(url, params);
}
/**
 * Function for constructing DELETE action which is used in redux-effects-fetch
 *
 * @private
 * @param {string} request url
 * @param {FetchConfig} config
 * @returns {Object} action object which is used for HTTP DELETE request
 */


function deleteRequest(url, config) {
  var params = getParams(config, 'DELETE');
  return request(url, params);
}
/**
 * Get request params
 *
 * @private
 * @param {FetchConfig} config
 * @param {string} method
 */


function getParams(config, method) {
  var paramsMethodLens = lensPath(['params', 'method']);
  var params = set(paramsMethodLens, method, config);
  return params;
}

/**
 * Convert object to query string
 * @private
 * @name toQueryString
 * @param {object}
 * @return {string} query string
 */

var toQueryString = pipe( // remove null-ish values
reject(isNil), ifElse(isEmpty, // return empty string if empty
always(''), // do the actual conversion
pipe( // convert to array of pairs ({a: 1, b: 2} -> [['a', 1], ['b', 2]])
toPairs, // join keys and values
map(join('=')), // join pairs
join('&'), // prepend with question mark
function (queryString) {
  return "?".concat(queryString);
})));

/**
 * @param {FetchConfig} config
 */

function fetchAvatars(config) {
  return getRequest('/avatars', config);
}
/**
 * Get current state of the BossFight
 * @param {FetchConfig} config
 */

function startBossFight(config) {
  return postRequest('/grid_battle', config);
}
/**
 * Get a list of Available Treasures in the BossFight
 * @param {FetchConfig} config
 */

function fetchAvailableBossFightTreasures(config) {
  return getRequest('/grid_battle/available_treasures', config);
}
/**
 * Get Boss Fight Action
 * @param {FetchConfig} config
 */

function generateBossFightBoxAction(config) {
  return postRequest('/grid_battle/box', config);
}
/**
 * Use weapon against boss
 * @param {number} id
 * @param {FetchConfig} config
 */

function useBossFightWeapon(id, config) {
  if (id == null) {
    throw new TypeError('id is required');
  }

  if (typeof id !== 'number') {
    throw new TypeError('id must be a number');
  }

  return deleteRequest("/grid_battle/weapons/".concat(id), config);
}
/**
 * Get region specific bossFight assets
 * @param {string}
 * @param {FetchConfig} config
 */

function fetchBossFightRegionAssets(regionKey, config) {
  if (regionKey == null) {
    throw new TypeError('regionKey is required');
  }

  if (typeof regionKey !== 'string') {
    throw new TypeError('regionKey must be a string');
  }

  return getRequest("/regions/".concat(regionKey, "/battle_assets"), config);
}
/**
 * @param {FetchConfig} config
 */

function fetchAllActiveCampaigns(config) {
  return getRequest('/ctool/campaigns', config);
}
/**
 * Fetch config data which is static
 * @param {FetchConfig} config
 */

function fetchConfig(config) {
  return getRequest('/config', config);
}
/**
 * Fetch config data which can change
 * @param {FetchConfig} config
 */

function fetchPublicConfig(config) {
  return getRequest('/public_config', config);
}
/**
 * Get all supported countries.
 * @param {FetchConfig} config
 */

function fetchCountries(config) {
  return getRequest('/countries/enabled', config);
}
/**
 * Get all enabled countries.
 * @param {FetchConfig} config
 */

function fetchExtendAccountCountries(config) {
  return getRequest('/countries/extended_account_enabled', config);
}
/**
 * Get current country code.
 * @param {FetchConfig} config
 */

function fetchCountryCode(config) {
  return getRequest('/country/lookup', config);
}
/**
 * @param {FetchConfig} config
 */

function fetchDate(config) {
  return getRequest('/date', config);
}
/**
 * Get deposit offers
 * @param {Object} params
 * @param {string} [params.affiliateId] affiliate id
 * @param {FetchConfig} config
 */

function fetchDepositOffers(params, config) {
  var queryString = toQueryString({
    affiliate_id: params.affiliateId
  });
  return getRequest("/deposit_offers".concat(queryString), config);
}
/**
 * @param {string} offerPackageId
 * @param {FetchConfig} [config]
 * @param {Object} [offerParams]
 * @returns {Promise}
 */

function claimDepositOffer(offerPackageId) {
  var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var offerParams = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var data = _objectSpread2({
    deposit_offer_package_id: offerPackageId
  }, offerParams.ttclid && offerParams.ttp ? {
    ttclid: offerParams.ttclid,
    ttp: offerParams.ttp,
    request_referrer: offerParams.requestReferrer,
    request_url: offerParams.requestUrl
  } : {});

  var params = {
    body: data
  };
  return postRequest('/deposit_offers', merge({
    params: params
  }, config));
}
/**
 * @param {FetchConfig} config
 */

function deleteClaimedDepositOffer(config) {
  return deleteRequest("/deposit_offer", config);
}
/**
 * @param {FetchConfig} config
 */

function fetchGameProviders(config) {
  return getRequest("/game_providers", config);
}
/**
 * @param {FetchConfig} config
 */

function fetchLeaderboard(config) {
  return getRequest('/division_table', config);
}
/**
 * Get total deposits crossbrand
 * @param {FetchConfig} config
 */

function fetchTotalDepositsCents(config) {
  return getRequest('/my/info/total_deposit_cents', config);
}
/**
 * Get IP info.
 * @param {FetchConfig} config
 */

function fetchIpInfo(config) {
  return getRequest('/ip_lookup', config);
}
/**
 * Adds a game to the logged-in players' quick games.
 * @param {string} provider
 * @param {string} key
 * @param {FetchConfig} config
 */

function addQuickGame(provider, key, config) {
  var params = {
    body: {
      provider: provider,
      key: key
    }
  };
  return postRequest('/games/quick', merge({
    params: params
  }, config));
}
/**
 * @param {Object} options
 * @param {number} options.id
 * @param {string} [options.type=browser] `browser` or `mobile-browser`, if no value is provided, will default to `browser`
 * @param {string} [options.elementId] - target DOM element ID
 * @param {string} [options.lobbyUrl] - used only if type is `mobile-browser`
 * @param {number} [options.realityCheckTimeRemaining] - Number of milliseconds before reality check should be displayed
 * @param {string} [options.limitsUrl] - URL to a page where player can set limits
 * @param {string} [options.betHistoryUrl] - URL to a page that shows the player's play history
 * @param {string} [options.freeGames] - used for proper redirection after using a FreeGamesVoucher
 * @param {FetchConfig} options.config
 */

function fetchGame(options) {
  var params = pickAll(['id', 'type', 'elementId', 'language', 'limitsUrl', 'lobbyUrl', 'betHistoryUrl', 'realityCheckTimeRemaining', 'config', 'origin', 'freeGames'], options);
  var queryString = toQueryString({
    element_id: params.elementId,
    language: params.language,
    lobby_url: params.lobbyUrl,
    limits_url: params.limitsUrl,
    origin: params.origin,
    bet_history_url: params.betHistoryUrl,
    reality_check_time_remaining: params.realityCheckTimeRemaining,
    type: params.type,
    free_games: params.freeGames
  });
  return getRequest("/games/".concat(params.id).concat(queryString), params.config);
}
/**
 * Get launch parameters for non-game entities
 * @param {Object} params
 * @param {string} params.id
 * @param {string} params.provider
 * @param {FetchConfig} config
 */

function fetchLaunchParameters(params, config) {
  return getRequest("/launch_parameters/".concat(params.provider, "/").concat(params.id), config);
}
/**
 * @param {Object} options
 * @param {string} [options.type=browser] `browser` or `mobile-browser`, if no value is provided, will default to `browser`
 * @param {number} [options.limit] number of games to be fetched, if no value provided, limit is infinity
 * @param {number} [options.offset] number indicating how many games to skip
 * @param {FetchConfig} config
 */

function fetchGames(options) {
  var params = pickAll(['type', 'limit', 'offset', 'config'], options);
  var queryString = toQueryString({
    type: params.type,
    limit: params.limit,
    offset: params.offset
  });
  return getRequest("/games".concat(queryString), params.config);
}
/**
 * Fetch game catalog index
 * @param {string} catalogId - Catalog ID
 * @param {FetchConfig} config
 */

function fetchGameCatalog(catalogId, config) {
  return getRequest("/game_catalogs/".concat(catalogId), config);
}
/**
 * Fetch game catalog categories
 * @param {Object} options
 * @param {number} options.categoryId Category ID
 * @param {'browser'|'mobile-browser'|'ios'} options.clientType Client type
 * @param {FetchConfig} config
 */

function fetchGameCatalogCategories(options, config) {
  var params = pickAll(['categoryId', 'clientType'], options);
  var queryString = toQueryString({
    clientType: params.clientType
  });
  return getRequest("/game_catalog_categories/".concat(params.categoryId).concat(queryString), config);
}
/**
 * Fetch game catalog sections
 * @param {Object} options
 * @param {number} options.sectionId Section ID
 * @param {'browser'|'mobile-browser'|'ios'} options.clientType Client type
 * @param {number} [options.limit] number of games to be fetched, if no value provided, limit is infinity
 * @param {number} [options.offset] number indicating how many games to skip
 * @param {FetchConfig} config
 */

function fetchGameCatalogSections(options, config) {
  var params = pickAll(['clientType', 'sectionId', 'limit', 'offset'], options);
  var queryString = toQueryString({
    clientType: params.clientType,
    limit: params.limit,
    offset: params.offset
  });
  return getRequest("/game_catalog_sections/".concat(params.sectionId).concat(queryString), config);
}
/**
 * @param {object} queryParams
 * @param {boolean} queryParams.mobile - Should support touch
 * @param {number} queryParams.limit - Limit games in response
 * @param {number} queryParams.offset - Indicating how many games to skip
 * @param {Array} queryParams.tags - Return games with specific tags like "slots"
 * @param {boolean} queryParams.recent - Return only recently played gamed
 * @param {boolean} queryParams.text - Return games matched by text snippet
 * @param {FetchConfig} config
 */

function searchGames(queryParams, config) {
  var params = {
    body: {
      q: queryParams
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/games/search', requestParams);
}
/**
 * Do a blitz spin
 * @param {object} options
 * @param {number} options.betAmountCents - The stake of the spin
 * @param {number} options.betLevel - The level of the spin
 * @param {string} options.betLines - Bet lines
 * @param {number} options.denomination - The denomination of the spin
 * @param {number} options.id - The id of the game to spin
 * @param {boolean} options.wantsFreeRounds - t/f if you want to play freespins or not
 * @param {string} options.spinAction - Spin action type, used for respins
 * @param {object} options.requestOptions - Free form object usually taken from BE
 * @param {FetchConfig} config
 */

function makeBlitzSpin(options, config) {
  var params = {
    body: merge({
      betAmountCents: options.betAmountCents,
      betLevel: options.betLevel,
      betLines: options.betLines,
      denomination: options.denomination,
      spinAction: options.spinAction,
      wantsFreeRounds: options.wantsFreeRounds
    }, options.requestOptions || {})
  };
  return postRequest("/games/".concat(options.id, "/blitz"), merge({
    params: params
  }, config));
}
/**
 * Ignore next occurrences of given spin action for Blitz game, i.e. avoid
 * getting notifications for specific bonus round type
 * @param {object} options
 * @param {string} options.spinAction - Spin action type to ignore
 * @param {number} options.id - The id of the game to ignore actions on
 */

function ignoreBlitzAction(options, config) {
  var params = {
    body: {
      ignored_action: options.spinAction
    }
  };
  return postRequest("/games/".concat(options.id, "/ignored_blitz_actions"), merge({
    params: params
  }, config));
}
/**
 * Reset any blitz actions ignored using ignoreBlitzAction
 * @param {number} id - The id of the game to reset actions on
 */

function resetBlitzActions(id, config) {
  return deleteRequest("/games/".concat(id, "/ignored_blitz_actions"), config);
}
/**
 * Get active items that the player can use.
 * @param {FetchConfig} config
 */

function fetchInventory(config) {
  return getRequest('/inventory', config);
}
/**
 * Get secret prizes that the player can use
 * @param {FetchConfig} config
 */

function fetchSecretPrizes(config) {
  return getRequest('/secret_prizes', config);
}
/**
 * Delete secret prize when it get's claimed
 * @param {number} id
 * @param {FetchConfig} config
 */

function deleteSecretPrize(id, config) {
  return deleteRequest("/secret_prizes/".concat(id), config);
}
/**
 * Mark inventory item as used.
 * @param {number} inventoryId
 * @param {FetchConfig} config
 */

function useItem(inventoryId, config) {
  return putRequest("/inventory/".concat(inventoryId), config);
}
/**
 * Reset treasure-chest count.
 * @param {FetchConfig} config
 */

function readInventoryNotifications(config) {
  return putRequest('/read_inventory_notifications', config);
}
/**
 * @param {string} language
 * @param {FetchConfig} config
 */

function saveLanguagePreference(language, config) {
  var params = {
    body: {
      address: {
        language: null
      },
      language: language
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return putRequest('/me', requestParams);
}
/**
 * Dismiss Deposit Threshold Prompt
 * @param {FetchConfig} config
 */

function dismissDepositThresholdPrompt(config) {
  return postRequest('/my/responsible_gaming_deposit_threshold_prompt', config);
}
/**
 * Dismiss Free Text Pop-Up
 * @param {number} id
 * @param {FetchConfig} config
 */

function dismissFreeTextPopup(id, config) {
  return putRequest("/my/messages/".concat(id), config);
}
/**
 * Dismiss Responsible Gaming Prompt
 * @param {FetchConfig} config
 */

function dismissResponsibleGamingPrompt(config) {
  return putRequest('/my/responsible_gaming_prompt', config);
}
/**
 * Dismiss Source of Funds
 * @param {FetchConfig} config
 */

function dismissSourceOfFunds(config) {
  return postRequest('/my/source_of_funds_request_attempts', config);
}
/**
 * Dismiss Source of Wealth
 * @param {FetchConfig} config
 */

function dismissSourceOfWealth(config) {
  return postRequest('/my/dismiss_source_of_wealth', config);
}
/**
 * Dismiss phone and email prompt
 * @param {FetchConfig} config
 */

function dismissPhoneAndEmailPrompt(config) {
  return putRequest('/my/dismiss_phone_and_email_prompt', config);
}
/**
 * Dismiss phone verification
 * @param {FetchConfig} config
 */

function dismissPhoneVerification(config) {
  return putRequest('/dismiss_phone_verification', config);
}
/**
 * Fetch a list of live casino tables
 * @param {Object} options
 * @param {string} [options.type=browser] `browser` or `mobile-browser`, if no value is provided, will default to `browser`
 * @param {Object} [options.config]
 */

function fetchLiveCasinoTables() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var queryString = toQueryString({
    type: options.type || 'browser'
  });
  return getRequest("/live_casino_tables".concat(queryString), options.config);
}
/**
 * @function fetchLiveStatistics
 * @deprecated
 * @param {FetchConfig} config
 */

/* istanbul ignore next: deprecated */

function fetchLiveStatisticsV2(config) {
  return getRequest('/live_statistics', config);
}
/**
 * @param {Object} options
 * @param {number} [options.limit] Max items to be returned. BE will return 3 if not passed
 * @param {FetchConfig} config
 */

function fetchLiveStatistics(options, config) {
  /* istanbul ignore next: dev-oriented code */
  if (arguments.length === 1) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('DEPRECATED', 'fetchLiveStatistics V2 API will be removed in next major release');
    }

    return fetchLiveStatisticsV2(options);
  }

  var queryString = toQueryString({
    limit: path(['limit'], options)
  });
  return getRequest("/live_statistics".concat(queryString), config);
}
/* eslint-disable camelcase */

/**
 * @param {FetchConfig} config
 */

function fetchPlayerGames(config) {
  return getRequest('/my/games', config);
}
/**
 * @param {FetchConfig} config
 */

function deleteGift(config) {
  return deleteRequest('/my/gift', config);
}
/**
 * @param {FetchConfig} config
 */

function claimGift(config) {
  return postRequest('/my/gift', config);
}
/**
 * @param {FetchConfig} config
 */

function fetchPlayerInfo(config) {
  return getRequest('/my/info', config);
}
/**
 * @param {FetchConfig} config
 */

function fetchPlayerRegion() {
  var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  /* istanbul ignore next */
  var normalizedSuccess = function normalizedSuccess(res) {
    var treasuresSchema = new schema.Entity('treasures');
    var normalizedTreasures = normalize(res.value.unfoundTreasures, [treasuresSchema]);
    var response = assocPath(['value', 'unfoundTreasures'], normalizedTreasures, res);
    return config.success(response);
  };

  return getRequest('/my/region', when(propEq('normalize', true), assoc('success', normalizedSuccess), config));
}
/**
 * @param {FetchConfig} config
 */

function fetchPlayerLimits(config) {
  return getRequest('/my/limits', config);
}
/**
 * @param {("15 minutes" | "30 minutes" | "60 minutes")} period
 * @param {FetchConfig} config
 */

function setRealityCheckSettings(period, config) {
  var requestBody = {
    reality_check_settings: {
      period: period
    }
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return putRequest('/my/reality_check_settings', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function removeRealityCheckSettings(config) {
  return deleteRequest('/my/reality_check_settings', config);
}
/**
 * Backwards compatibility alias for `setRealityCheckSettings`
 * @deprecated
 * @param {("15 minutes" | "30 minutes" | "60 minutes")} period
 * @param {FetchConfig} config
 */

function requestPlayerRealityCheckSettings(period, config) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('DEPRECATED', 'Renamed to `setRealityCheckSettings`');
  }

  return setRealityCheckSettings(period, config);
}
/**
 * @param {("bet"|"loss"|"deposit")} type
 * @param {("day"|"week"|"month")} period
 * @param {number} amountCents
 * @param {FetchConfig} config
 */

function requestPlayerLimit(type, period, amountCents, config) {
  var requestBody = {
    limits: {
      type: type,
      period: period,
      value_cents: amountCents
    }
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return putRequest('/my/limits', requestParams);
}
/**
 * @param {Object} limits
 * @param {("bet"|"loss"|"deposit")} limits.type
 * @param {("day"|"week"|"month")} limits.period
 * @param {number} limits.amountCents
 * @param {FetchConfig} config
 */

function requestPlayerLimits(limits, config) {
  var requestBody = {
    limits: map(function (limit) {
      return {
        type: limit.type,
        period: limit.period,
        value_cents: limit.amountCents
      };
    }, limits)
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return putRequest('/my/limits', requestParams);
}
/**
 * @param {("bet"|"loss"|"deposit")} type
 * @param {("day"|"week"|"month")} period
 * @param {FetchConfig} config
 */

function cancelPlayerLimit(type, period, config) {
  var requestBody = {
    limits: {
      type: type,
      period: period,
      value_cents: null
    }
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return putRequest('/my/limits', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function confirmPlayerLimits(config) {
  return putRequest('/my/unset_deposit_limits_popup', config);
} // TODO: remove wrapping function with incorrect name
// along with next BREAKING CHANGE

/**
 * @param {FetchConfig} config
 */

function confirmUnsetPlayerLimits(config) {
  return confirmPlayerLimits(config);
}
/**
 * @param {("1 day"|"7 days"|"2 weeks"|"6 weeks")} period
 * @param {string} password
 * @param {FetchConfig} config
 */

function requestPlayerTimeOut(period, password, config) {
  var requestBody = {
    player: {
      password: password,
      expires_in: period
    }
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return postRequest('/my/timeout', requestParams);
}
/**
 * @param {("6 months"|"1 year"|"2 years"|"3 years"|"4 years"|"5 years")} period
 * @param {string} type
 * @param {FetchConfig} config
 */

function requestPlayerSelfExclusion(period, password, config) {
  var requestBody = {
    player: {
      password: password,
      expires_in: period
    }
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return postRequest('/my/self_exclusion', requestParams);
}
/**
 * @param {string} key
 * @param {FetchConfig} config
 */

function updatePlayerWorlds(key, config) {
  var requestParams = merge({
    params: {
      body: {
        world_key: key
      }
    }
  }, config);
  return putRequest('/my/world', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function updateRegionProgress(config) {
  return putRequest('/my/region', config);
}
/**
 * @param {number} treasureId
 * @param {FetchConfig} config
 */

function deleteTreasure(treasureId, config) {
  return deleteRequest("/my/treasures/".concat(treasureId), config);
}
/**
 * @param {number} itemsPerPage
 * @param {object} params
 * @param {FetchConfig} config
 */

function fetchGamblingHistory(itemsPerPage, params, config) {
  var requestParams = {
    per_page: itemsPerPage,
    start_date: params.startDate,
    end_date: params.endDate,
    cursor_timestamp: params.cursor && params.cursor.timestamp,
    include_totals: params.includeTotals,
    exclude_sport: params.excludeSport,
    exclude_casino: params.excludeCasino
  };
  return getRequest("/my/game_transactions".concat(toQueryString(requestParams)), config);
}
/**
 * @param {number} campaignId
 * @param {FetchConfig} config
 */

function optInCampaign(campaignId, config) {
  var requestBody = {
    campaign_id: campaignId
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return postRequest('/my/opted_in_campaigns', requestParams);
}
/**
 * @param {number} campaignId
 * @param {FetchConfig} config
 */

function optOutCampaign(campaignId, config) {
  var requestBody = {
    campaign_id: campaignId
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return deleteRequest('/my/opted_in_campaigns', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function optOutAllDepositCampaigns(config) {
  var requestBody = {
    opt_out_from_all_deposit_campaigns: true
  };
  var requestParams = merge({
    params: {
      body: requestBody
    }
  }, config);
  return deleteRequest('/my/opted_in_campaigns', requestParams);
}
/* eslint-enable camelcase */

/**
 * @param {FetchConfig} config
 */

function fetchAllNotifications(config) {
  return getRequest('/notifications', config);
}
/**
 * @param {string} kind
 * @param {FetchConfig} config
 */

function fetchNotificationsByKind(kind, config) {
  return getRequest("/notifications?kind=".concat(kind), config);
}
/**
 * @param {number} notificationId
 * @param {FetchConfig} config
 */

function deleteNotification(notificationId, config) {
  return deleteRequest("/notifications/".concat(notificationId), config);
}
/**
 * @param {number} affiliateId
 * @param {string} brandKey
 * @param {string} countryCode
 * @param {FetchConfig} config
 */

function fetchWelcomeOffers(affiliateId, brandKey, countryCode, config) {
  return getRequest("/offers?affiliateId=".concat(affiliateId, "&brandKey=").concat(brandKey, "&countryCode=").concat(countryCode), config);
}
/**
 * Sets onboarding to completed
 * @param {FetchConfig} config
 */

function finishOnboarding(config) {
  return postRequest('/my/onboarding', config);
}
/**
 * @param {string} token - Password reset token
 * @param {string} password
 * @param {string} passwordConfirmation
 * @param {FetchConfig} config
 */

function updatePassword(token, password, passwordConfirmation, config) {
  if (pathEq(['version'], 2, config)) {
    return updatePasswordV2(token, password, passwordConfirmation, config);
  } else {
    return updatePasswordV1(token, password, passwordConfirmation, config);
  }
}
/**
 * @param {string} token - Password reset token
 * @param {string} password
 * @param {string} passwordConfirmation
 * @param {FetchConfig} config
 */

function updatePasswordV1(token, password, passwordConfirmation, config) {
  var params = {
    body: {
      token: token,
      password: password,
      passwordConfirmation: passwordConfirmation
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return putRequest('/password', requestParams);
}
/**
 * @param {string} token - Password reset token
 * @param {string} password
 * @param {string} passwordConfirmation
 * @param {FetchConfig} config
 */

function updatePasswordV2(token, password, passwordConfirmation, config) {
  var params = {
    body: {
      password: password,
      passwordConfirmation: passwordConfirmation
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return putRequest("/password_resets/".concat(token), requestParams);
}
/**
 * @param {string} oldPassword
 * @param {string} newPassword
 * @param {string} newPasswordConfirmation
 * @param {FetchConfig} config
 */

function changePassword(oldPassword, newPassword, newPasswordConfirmation, config) {
  var params = {
    body: {
      player: {
        old_password: oldPassword,
        password: newPassword,
        password_confirmation: newPasswordConfirmation
      }
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return putRequest('/my/passwords', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function fetchDepositInformation(config) {
  return getRequest('/deposit/information', config);
}
/**
 * @param {FetchConfig} config
 */

function fetchWithdrawInformation(config) {
  return getRequest('/payment/withdrawable', config);
}
/**
 * @param {FetchConfig} config
 */

function fetchNetDeposit(config) {
  return getRequest('/receipts/net_deposits', config);
}
/**
 * Gets total deposits and withdraws for the last 12 months
 * @param {FetchConfig} config
 */

function fetchTransactionTotals(config) {
  return getRequest('/receipts/totals', config);
}
/**
 * @param {integer} page - Must be greater than 0
 * @param {FetchConfig} config
 */

function fetchReceipts(page, config) {
  if (page !== parseInt(page, 10)) {
    throw new TypeError('page must be an integer');
  }

  if (page <= 0) {
    throw new TypeError('page must be greater than 0');
  }

  return getRequest("/receipts?page=".concat(page), config);
}
/**
 * @param {number} amount
 * @param {string} preferredWelcomeOfferType ('none', 'money' or 'freespins')
 * @param {string} toCurrency
 * @param {FetchConfig} config
 */

function convertDepositAmount(amount, preferredWelcomeOfferType, toCurrency, config) {
  var params = {
    body: {
      amount: amount,
      preferredWelcomeOfferType: preferredWelcomeOfferType,
      toCurrency: toCurrency
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/exchange/deposit', requestParams);
}
function convertWithdrawAmount(amount, toCurrency, config) {
  var params = {
    body: {
      amount: amount,
      toCurrency: toCurrency
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/exchange/withdraw', requestParams);
}
/**
 * @param {number} amount
 * @param {FetchConfig} config
 */

function convertBalanceAmount(amount, config) {
  var params = {
    body: {
      amount: amount
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/exchange/deposit_amount', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function requestSmsVerification(config) {
  return postRequest('/my/phone_verification', config);
}
/**
 * @param {string} token
 * @param {FetchConfig} config
 */

function verifyPhoneNumber(token, config) {
  return postRequest("/my/phone_verification/verify?token=".concat(token), config);
}
/**
 * @param {FetchConfig} config
 */

function requestEmailVerificationCode(config) {
  return postRequest('/my/email_verification', config);
}
/**
 * @param {string} token
 * @param {FetchConfig} config
 */

function verifyEmail(token, config) {
  var params = {
    body: {
      token: token
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/my/email_verification/verify', requestParams);
} // TODO: describe optional props if needed

/**
 * Request configuration object
 * @typedef {Object} AddressData
 * @prop {string} firstName
 * @prop {string} lastName
 * @prop {string} street
 * @prop {string} zip
 * @prop {string} city
 * @prop {string} country
 */
// TODO: describe optional props if needed

/**
 * @typedef {Object} UserData
 * @prop {string} username
 * @prop {string} email
 * @prop {string} password
 * @prop {string} passwordConfirmation
 * @prop {string} birthdate
 * @prop {string} firstName
 * @prop {string} lastName
 * @prop {string} street
 * @prop {string} zip
 * @prop {string} city
 * @prop {string} countryCode
 * @prop {string} gender
 * @prop {string} netrefererBTag
 */

/**
 * Get player profile including account balance and address
 * @param {FetchConfig} config
 */

function fetchMe(config) {
  return getRequest('/me', config);
}
/**
 * @param {UserData} userData
 * @param {FetchConfig} config
 */

function updateMe(userData, config) {
  var requestParams = merge({
    params: {
      body: userData
    }
  }, config);
  return putRequest('/me', requestParams);
}
/**
 * @param {string} countryCallingCode
 * @param {string} phoneNumber
 * @param {FetchConfig} config
 */

function validatePhoneNumber(countryCallingCode, phone, config) {
  var params = {
    country_calling_code: countryCallingCode,
    phone: phone
  };
  return postRequest("/validations/phone".concat(toQueryString(params)), config);
}
/**
 * @param {AddressData} addressData
 * @param {FetchConfig} config
 */

function validateAddress(addressData, config) {
  var requestParams = merge({
    params: {
      body: addressData
    }
  }, config);
  return postRequest('/validations/address', requestParams);
}
/**
 * @param {UserData} userData
 * @param {FetchConfig} config
 */

function registerPlayer(userData, config) {
  var requestParams = merge({
    params: {
      body: userData
    }
  }, config);
  return postRequest('/register', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function lockPlayer(config) {
  return postRequest('/my/iovation_verification_failures/', config);
}
/**
 * Validate uniqueness of email
 * @param {string} email
 * @param {FetchConfig} config
 */

function validateEmail(email, config) {
  var params = {
    body: {
      attribute: 'email',
      value: email
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/validate/player', requestParams);
}
/**
 * Validate uniqueness of username
 * @param {string} username
 * @param {FetchConfig} config
 */

function validateUsername(username, config) {
  var params = {
    body: {
      attribute: 'username',
      value: username
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/validate/player', requestParams);
}
/**
 * @param {string} countryCode
 * @param {boolean} excludeDrawerOnly
 * @param {FetchConfig} config
 */

function fetchPromotions(countryCode, config, excludeDrawerOnly) {
  if (excludeDrawerOnly) {
    return getRequest("/promotions".concat(toQueryString({
      country_code: countryCode,
      exclude_drawer_only: excludeDrawerOnly
    })), config);
  }

  return getRequest("/promotions".concat(toQueryString({
    country_code: countryCode
  })), config);
}
/**
 * @param {boolean} real - Return only recently played games
 * @param {FetchConfig} config
 */

function fetchQuickGames(real, config) {
  return getRequest("/games/quick?real=".concat(real), config);
}
/**
 * @param {string} username
 * @param {string} password
 * @param {string} seonSessionId - fingerprint value generated by seon
 * @param {string} clientType - either '' or 'mobile-browser'
 * @param {FetchConfig} config
 */

function login(username, password, clientType, config, seonSessionId) {
  var params = {
    body: {
      login: username,
      password: password,
      clientType: clientType,
      seonSessionId: seonSessionId
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/session', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function logout(config) {
  return deleteRequest('/session', config);
}
/**
 * Get current session
 * @param {FetchConfig} config
 */

function fetchSession(config) {
  return getRequest('/session', config);
}
/**
 * Get current session statistics
 * @param {FetchConfig} config
 */

function fetchSessionStats(config) {
  return getRequest('/my/session_stats', config);
}
/**
 * @param {string} email
 * @param {FetchConfig} config
 */

function resetPassword(email, config) {
  var params = {
    body: {
      handle: email
    }
  };
  var requestParams = merge({
    params: params
  }, config);

  if (pathEq(['version'], 2, config)) {
    return postRequest('/password_resets', requestParams);
  }

  return postRequest('/password', requestParams);
}
/**
 * @param {Object} data
 * @param {string} data.employment - Listing type of employment.
 * @param {string} data.occupation - Listing user occupation.
 * @param {string} data.salary - Listing user salary level.
 * @param {Array} data.sourcesOfIncome - Listing sources of income.
 * @param {string} data.nationality - Listing nationalities.
 * @param {string} data.placeOfBirth - Listing place of birthg.
 * @param {string} data.idNumber - Input of user ID number.
 * @param {string} data.anticipatedYearlyDeposits - Listing user anticpated yearly deposit level.
 * @param {Array} data.sourcesOfFunds - Listing sources of funds.
 * @param {Array} data.fundsCountryOrigin - Listing countries where funds come from.
 * @param {FetchConfig} config
 */

function submitSourceOfFunds(data, config) {
  var params = {
    body: {
      employment: data.employment,
      occupation: data.occupation,
      salary: data.salary,
      sources_of_income: data.sourcesOfIncome,
      nationality: data.nationality,
      placeOfBirth: data.placeOfBirth,
      idNumber: data.idNumber,
      anticipatedYearlyDeposits: data.anticipatedYearlyDeposits,
      sourcesOfFunds: data.sourcesOfFunds,
      fundsCountryOrigin: data.fundsCountryOrigin
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/source_of_funds', requestParams);
}
/**
 * @param {FetchConfig} config
 */

function fetchStoreItems(config) {
  return getRequest('/store_items', config);
}
/**
 * @param {FetchConfig} config
 */

function fetchRealMoneyStoreItems(config) {
  return getRequest('/store_items?sold_for_real_money=true', config);
}
/**
 * @param {(number|Object)} data
 * @param {number} data.id
 * @param {boolean} [data.blitz] - item should be blitz
 * @param {string} [data.stakeIndex] - chosen stake index in the array of stakes
 * @param {string} [data.transactionId] - transaction ID
 * @param {FetchConfig} config
 */

function buyItem(data, config) {
  if (data && (data.blitz || !isNil(data.stakeIndex))) {
    var params = {
      body: {
        blitz: data.blitz,
        stakeIndex: data.stakeIndex,
        transactionId: data.transactionId
      }
    };
    var requestParams = merge({
      params: params
    }, config);
    return postRequest("/store_items/".concat(data.id, "/buy"), requestParams);
  }

  return postRequest("/store_items/".concat(data, "/buy"), config);
}
/**
 * @param {FetchConfig} config
 */

function markStoreAsSeen(config) {
  return deleteRequest('/my/menu_items/store', config);
}
/**
 * @param {FetchConfig} config
 */

function acceptPrivacyPolicy(config) {
  return putRequest('/accept_tc', config);
}
/**
 * @param {FetchConfig} config
 */

function acceptBonusFunds(config) {
  return putRequest('/my/deposit_offer_prompt', config);
}
/**
 * @param {FetchConfig} config
 */

function declineBonusFunds(config) {
  var params = {
    body: {
      declined_bonus: true
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return putRequest('/my/deposit_offer_prompt', requestParams);
}
/**
 * @param {string} key - unique identifier for world
 * @param {FetchConfig} config
 */

function fetchWorldRegions(key) {
  var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  /* istanbul ignore next */
  var normalizedSuccess = function normalizedSuccess(res) {
    var regionsSchema = new schema.Entity('regions', {}, {
      idAttribute: 'key'
    });
    var normalizedResult = normalize(res.value.result, [regionsSchema]);
    var response = assocPath(['value', 'result'], normalizedResult, res);
    return config.success(response);
  };

  return getRequest("/worlds/".concat(key, "/regions"), when(propEq('normalize', true), assoc('success', normalizedSuccess), config));
}
/**
 * @param {FetchConfig} config
 */

function fetchWorlds() {
  var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  /* istanbul ignore next */
  var normalizedSuccess = function normalizedSuccess(res) {
    var worldsSchema = new schema.Entity('worlds', {}, {
      idAttribute: 'key'
    });
    var normalizedResult = normalize(res.value.result, [worldsSchema]);
    var response = assocPath(['value', 'result'], normalizedResult, res);
    return config.success(response);
  };

  return getRequest('/worlds', when(propEq('normalize', true), assoc('success', normalizedSuccess), config));
}
/**
 * @param {FetchConfig} config
 */

function finishWorld(config) {
  return postRequest('/finish_world', config);
}
/**
 * @param {number} amount
 * @param {FetchConfig} config
 */

function processWithdrawals(amount, config) {
  var params = {
    body: {
      amount: amount
    }
  };
  var requestParams = merge({
    params: params
  }, config);
  return postRequest('/withdrawals', requestParams);
}
/* istanbul ignore next */

function injectLinks(response) {
  var links = {
    mga: ['curacao'],
    sga: ['limits', 'timeout', 'selftest'],
    ukgc: ['curacao']
  };
  return assocPath(['value', 'links'], links[response.value.licenseId], response);
}
/**
 * Get jurisdictions
 * @param {FetchConfig} config
 */


function fetchJurisdiction(config) {
  var noop = always(undefined);
  var success = pathOr(noop, ['success'], config);
  return getRequest('/jurisdiction', assoc('success', pipe(injectLinks, success), config));
}
function fetchLocks(config) {
  return getRequest('/my/locks', config);
}
/**
 * @param {FetchConfig} config
 */

function acceptTermsAndConditions(config) {
  return putRequest('/terms_and_conditions', config);
}
/**
 * @param {Object} options Options for the post body
 * @param {number} options.amountCents Price amount in cents
 * @param {array} options.drawingDays Drawing days: ex ["WE","SU"]
 * @param {number} options.duration How many many weeks the purchase should be repeated
 * @param {array} options.firstDrawIds Draw id/s of the draws that should be purchased (Max one week from now, draws for week 2 or 3 will be handled in the backend in case of duration 2 or 3)
 * @param {string} options.firstDrawId Draw id of the ones that should be purchased (old alternative way to firstDrawIds)
 * @param {number} options.lines How many lines that should be purchased
 * @param {string} options.productId Which product the ticket/s should be purchased from
 * @param {FetchConfig} config
 */

function purchaseLottoTicket(options, config) {
  var params = {
    body: {
      amountCents: options.amountCents,
      drawingDays: options.drawingDays,
      duration: options.duration,
      firstDrawIds: options.firstDrawId ? [options.firstDrawId] : options.firstDrawIds,
      lines: options.lines,
      lottery: options.productId
    }
  };
  return postRequest('/lottoland/lottery_tickets', merge({
    params: params
  }, config));
}
/**
 * @param {string} currency
 * @param {FetchConfig} config
 */

function fetchLottoPrices(currency, config) {
  var queryString = toQueryString({
    currency: currency
  });
  return getRequest("/lottoland/lottery_tickets/prices".concat(queryString), config);
}
/**
 * @param {Object} options Options for the post body
 * @param {number} options.id Id of the lottery bundle to be purchased
 * @param {FetchConfig} config
 */

function purchaseLottoBundle(options, config) {
  var params = {
    body: {
      id: options.id
    }
  };
  return postRequest('/lottoland/lottery_bags', merge({
    params: params
  }, config));
}
/**
 * @param {FetchConfig} config
 */

function fetchLottoBundles(config) {
  return getRequest("/lottoland/lottery_bags", config);
}
/**
 * Send SMS notifications to the given number (if the player wins on any ticket related to the transactionId).
 * @param {object} options Transaction id and mobile phone number
 * @param {string} options.transaction_id The id of the purchase transaction (can point to one or several tickets)
 * @param {string} options.phone_number The mobile phone number to which as SMS will be sent
 */

function requestLottoWinSmsNotifications(options, config) {
  var params = {
    body: {
      transaction_id: options.transactionId,
      phone_number: options.phoneNumber
    }
  };
  return postRequest('/lottoland/phone_notifications', merge({
    params: params
  }, config));
}
/**
 * Fetch players custom games
 * @param {FetchConfig} config
 */

function fetchPlayerCustomGames(config) {
  return getRequest('/my/picked_games', config);
}
/**
 * Set players custom games
 * @param {Array} gameIds array of game ids
 * @param {FetchConfig} config
 */

function updatePlayerCustomGames(gameIds, config) {
  var params = {
    body: {
      picks: gameIds
    }
  };
  return putRequest('/my/picked_games', merge({
    params: params
  }, config));
}
/**
 * Fetch metadata for a particular URL
 * @param {object} params url
 * @param {string} params.url The URL for which the data is requested
 * @param {FetchConfig} config
 */

function fetchSeoDescription(params, config) {
  return getRequest("/seo_descriptions/?url=".concat(params.url), config);
}
/**
 * @param {FetchConfig} config
 */

function claimBetBack(config) {
  return putRequest('/my/betback', config);
}
/**
 * Mark all current user's notifications as seen
 * @param {FetchConfig} config
 */

function markNotificationsAsSeen(config) {
  return putRequest('/mark_notifications_as_seen', config);
}
/**
 * Get affiliate data
 * @param {Object} params
 * @param {string} params.btag NetRefer BTag
 * @param {FetchConfig} config
 */

function fetchAffiliateData(params, config) {
  var queryString = toQueryString({
    btag: params.btag
  });
  return getRequest("/affiliates".concat(queryString), config);
}
/**
 * Request sendout of sms with verification-code to supplied number
 * @param {Object} data
 * @param {string} data.countryCallingCode Country calling code Ex. '+46'
 * @param {string} data.phone Phone number without CCC prefix Ex. '0707112233'
 * @param {string} data.language Language to receive sms in, Ex. 'en'
 * @param {FetchConfig} config
 */

function requestPhoneVerificationCode(data, config) {
  var params = {
    body: {
      country_calling_code: data.countryCallingCode,
      phone: data.phone,
      language: data.language
    }
  };
  return postRequest('/phone_verification', merge({
    params: params
  }, config));
}
/**
 * Dismiss high deposit limit prompt
 * @param {FetchConfig} config
 */

function dismissHighDepositLimitPrompt(config) {
  return postRequest('/my/high_deposit_limit_prompt', config);
}
/**
 * Fetches Shufti Pro generated iframe URL
 * @param {Object} options
 * @param {string} options.redirectUrl
 * @param {('identity'|'address'|'additional')} options.kind
 * @param {FetchConfig} config
 */

function fetchShuftiProIframeUrl(options, config) {
  var params = {
    body: {
      redirect_url: options.redirectUrl,
      kind: options.kind
    }
  };
  return postRequest('/my/shufti_documents', merge({
    params: params
  }, config));
}
/**
 * Fetches Shufti Pro documents for player
 * @param {FetchConfig} config
 */

function fetchShuftiProDocuments(config) {
  return getRequest('/my/shufti_documents', config);
}
/**
 * @param {FetchConfig} config
 */

function fetchGameTransactionsTotals(config) {
  return getRequest("/my/game_transactions/totals", config);
}
/**
 * @param {FetchConfig} config
 */

function dismissCustomerDueDiligencePrompt(config) {
  return postRequest('/my/dismiss_cdd', config);
}
/**
 * Get all countries with their status and calling codes from v2 endpoint.
 * @param {FetchConfig} config
 */

function fetchAllCountries(config) {
  return getRequest('/countries', _objectSpread2(_objectSpread2({}, config), {}, {
    version: 2
  }));
}

/* global __API_URL__ */

var getSecondLevelDomain = pipe(match(/[a-z][\da-z-]+\.\w+$/i), head);
function getApiUrl(hostname) {
  /* istanbul ignore next: dev-only code */
  if (typeof __API_URL__ === 'string') {
    // eslint-disable-next-line no-console
    console.warn('[Deprecation] __API_URL__ global usage is discouraged; prefer process.env.API_URL');
    return __API_URL__;
  }
  /* istanbul ignore next: dev-only code */


  if (typeof process.env.API_URL === 'string') {
    return process.env.API_URL;
  }

  var secondLevelDomain = getSecondLevelDomain(hostname);

  if (secondLevelDomain) {
    return "//clients.".concat(secondLevelDomain);
  } else {
    /* istanbul ignore next: dev-only code */
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('API URL cannot be detected locally.', 'Set API_URL environment variable.', 'You can also use .env to persist configuration.', 'More information can be found in project’s README.md');
    }

    return null;
  }
}

/**
 * Debounces fetch action by 500ms if a search query has the name "search".
 * - To be used with debounceMiddleware from @rushplay/common
 * - Should be placed before api.middleware in applyMiddleware from Redux
 * @param {Object} action Redux action
 * @returns {?Object}
 * @example
 * import * as api from '@rushplay/api-client'
 * import { applyMiddleware } from 'redux'
 *
 * applyMiddleware(
 *   debounceMiddleware([
 *     api.shouldFetchDebounce
 *   ]),
 *   api.middleware({...})
 * )
 */

function shouldFetchDebounce(action) {
  if (pathEq(['type'], 'EFFECT_COMPOSE', action) && pathEq(['payload', 'type'], EFFECT_FETCH_API, action) && getGameQueryByName('search', action)) {
    return {
      key: path(['payload', 'payload', 'url'], action),
      time: 500
    };
  }
}
/**
 * Checks if action payload body has the given game query name
 * @private
 * @param {Object} action
 * @param {String} name
 * @returns {Boolean}
 */

function getGameQueryByName(name, action) {
  var params = JSON.parse(pathOr('{}', ['payload', 'params', 'body'], action.payload));
  return Boolean(path(['q', name], params));
}

export { fetchAvatars, startBossFight, fetchAvailableBossFightTreasures, generateBossFightBoxAction, useBossFightWeapon, fetchBossFightRegionAssets, fetchAllActiveCampaigns, fetchConfig, fetchPublicConfig, fetchCountries, fetchExtendAccountCountries, fetchCountryCode, fetchDate, fetchDepositOffers, claimDepositOffer, deleteClaimedDepositOffer, fetchGameProviders, fetchLeaderboard, fetchTotalDepositsCents, fetchIpInfo, addQuickGame, fetchGame, fetchLaunchParameters, fetchGames, fetchGameCatalog, fetchGameCatalogCategories, fetchGameCatalogSections, searchGames, makeBlitzSpin, ignoreBlitzAction, resetBlitzActions, fetchInventory, fetchSecretPrizes, deleteSecretPrize, useItem, readInventoryNotifications, saveLanguagePreference, dismissDepositThresholdPrompt, dismissFreeTextPopup, dismissResponsibleGamingPrompt, dismissSourceOfFunds, dismissSourceOfWealth, dismissPhoneAndEmailPrompt, dismissPhoneVerification, fetchLiveCasinoTables, fetchLiveStatisticsV2, fetchLiveStatistics, fetchPlayerGames, deleteGift, claimGift, fetchPlayerInfo, fetchPlayerRegion, fetchPlayerLimits, setRealityCheckSettings, removeRealityCheckSettings, requestPlayerRealityCheckSettings, requestPlayerLimit, requestPlayerLimits, cancelPlayerLimit, confirmPlayerLimits, confirmUnsetPlayerLimits, requestPlayerTimeOut, requestPlayerSelfExclusion, updatePlayerWorlds, updateRegionProgress, deleteTreasure, fetchGamblingHistory, optInCampaign, optOutCampaign, optOutAllDepositCampaigns, fetchAllNotifications, fetchNotificationsByKind, deleteNotification, fetchWelcomeOffers, finishOnboarding, updatePassword, updatePasswordV1, updatePasswordV2, changePassword, fetchDepositInformation, fetchWithdrawInformation, fetchNetDeposit, fetchTransactionTotals, fetchReceipts, convertDepositAmount, convertWithdrawAmount, convertBalanceAmount, requestSmsVerification, verifyPhoneNumber, requestEmailVerificationCode, verifyEmail, fetchMe, updateMe, validatePhoneNumber, validateAddress, registerPlayer, lockPlayer, validateEmail, validateUsername, fetchPromotions, fetchQuickGames, login, logout, fetchSession, fetchSessionStats, resetPassword, submitSourceOfFunds, fetchStoreItems, fetchRealMoneyStoreItems, buyItem, markStoreAsSeen, acceptPrivacyPolicy, acceptBonusFunds, declineBonusFunds, fetchWorldRegions, fetchWorlds, finishWorld, processWithdrawals, fetchJurisdiction, fetchLocks, acceptTermsAndConditions, purchaseLottoTicket, fetchLottoPrices, purchaseLottoBundle, fetchLottoBundles, requestLottoWinSmsNotifications, fetchPlayerCustomGames, updatePlayerCustomGames, fetchSeoDescription, claimBetBack, markNotificationsAsSeen, fetchAffiliateData, requestPhoneVerificationCode, dismissHighDepositLimitPrompt, fetchShuftiProIframeUrl, fetchShuftiProDocuments, fetchGameTransactionsTotals, dismissCustomerDueDiligencePrompt, fetchAllCountries, RESPONSE_CACHED, START_TRACKING_REQUEST, STOP_TRACKING_REQUEST, cacheResponse, startTrackingRequest, stopTrackingRequest, reducer, getPendingRequests, getCachedResponse, EFFECT_FETCH_API, middleware, getApiUrl, shouldFetchDebounce };
//# sourceMappingURL=index.es.js.map
